import { useEffect, useRef } from 'react';
import useAuraWebSocket from '../../hooks/useAuraWebSocket';

/**
 * Sanitize object to remove circular references and non-serializable values
 */
function sanitizeSignal(signal) {
  if (!signal || typeof signal !== 'object') return signal;
  
  const visited = new WeakSet();
  
  function sanitize(obj) {
    if (obj == null) return obj;
    if (typeof obj !== 'object') return obj;
    if (visited.has(obj)) return '[Circular]';
    
    visited.add(obj);
    
    if (Array.isArray(obj)) {
      return obj.map(sanitize);
    }
    
    const sanitized = {};
    for (const [key, value] of Object.entries(obj)) {
      // Skip private properties and functions
      if (key.startsWith('_') || typeof value === 'function') continue;
      
      // Skip circular references (only check objects)
      if (typeof value === 'object' && value !== null && visited.has(value)) {
        sanitized[key] = '[Circular]';
        continue;
      }
      
      sanitized[key] = sanitize(value);
    }
    
    return sanitized;
  }
  
  return sanitize(signal);
}

/**
 * ProactiveAlerts component listens for real-time market updates
 * via WebSocket and triggers alerts when signals change or important events occur.
 * 
 * Phase 4: Now uses dedicated WebSocket connection for real-time alerts.
 */
export default function ProactiveAlerts({ onAlert, lastSignalData, enabled = true }) {
  const previousSignalRef = useRef(null);
  const alertedSignalIdsRef = useRef(new Set()); // Track which signals have been alerted

  // Use WebSocket hook for real-time alerts
  const { connectionError } = useAuraWebSocket(
    (alert) => {
      // Handle WebSocket alerts
      if (onAlert && alert) {
        onAlert(alert);
      }
    },
    enabled
  );

  // Helper function to create a unique signal identifier for comparison
  const getSignalId = (signal) => {
    if (!signal) return null;
    // Use signal_id if available, otherwise create a hash from key fields
    if (signal.signal_id) return signal.signal_id;
    // Create a stable identifier from key signal fields (ignore timestamp)
    const keyFields = {
      instrument: signal.instrument,
      signal_type: signal.signal_type,
      entry_price: signal.entry_price,
      target_price: signal.target_price,
      stop_loss: signal.stop_loss
    };
    return JSON.stringify(keyFields);
  };

  // Fallback: Also check lastSignalData for immediate updates (backward compatibility)
  useEffect(() => {
    if (!lastSignalData || !enabled) return;

    // Detect signal changes
    const currentSignal = lastSignalData.signal;
    if (!currentSignal) return;

    // Get unique identifier for current signal
    const currentSignalId = getSignalId(currentSignal);
    
    // Check if we've already alerted for this signal
    if (currentSignalId && alertedSignalIdsRef.current.has(currentSignalId)) {
      // Signal already alerted, skip
      return;
    }

    const prevSignal = previousSignalRef.current;
    const prevSignalId = getSignalId(prevSignal);

    // Only alert if this is a new signal (different ID)
    if (currentSignalId && currentSignalId !== prevSignalId) {
      // Generate alert message
      const alertMessage = generateAlertMessage(currentSignal, lastSignalData);
      if (alertMessage && onAlert) {
        // Mark this signal as alerted
        if (currentSignalId) {
          alertedSignalIdsRef.current.add(currentSignalId);
        }
        
        onAlert({ 
          message: alertMessage, 
          type: 'signal', 
          data: sanitizeSignal(currentSignal), // Sanitize before passing
          priority: 'high'
        });
      }
      // Store sanitized signal to avoid circular references
      previousSignalRef.current = sanitizeSignal(currentSignal);
    }

    // Update previous signal reference with sanitized data
    if (currentSignal) {
      previousSignalRef.current = sanitizeSignal(currentSignal);
    }

    // Check for armed setups (only alert once per setup)
    const strategyProgress = lastSignalData.strategy_progress || [];
    const armedSetups = strategyProgress.filter(s => s.status === 'Armed');
    
    if (armedSetups.length > 0) {
      const prevArmedSetups = previousSignalRef.current?.armed_setups || [];
      const prevArmedNames = new Set(prevArmedSetups.map(s => s.strategy_name));
      
      const newArmed = armedSetups.find(setup => {
        return !prevArmedNames.has(setup.strategy_name);
      });

      if (newArmed && onAlert) {
        const message = `Alert! A ${newArmed.strategy_name} setup is now armed. ${newArmed.reasoning || 'Watch for confirmation.'}`;
        onAlert({
          message,
          type: 'setup_armed',
          data: sanitizeSignal(newArmed), // Sanitize before passing
          priority: 'medium'
        });
        
        // Track this armed setup to prevent duplicate alerts
        if (!previousSignalRef.current) {
          previousSignalRef.current = {};
        }
        if (!previousSignalRef.current.armed_setups) {
          previousSignalRef.current.armed_setups = [];
        }
        previousSignalRef.current.armed_setups.push({
          strategy_name: newArmed.strategy_name,
          ...sanitizeSignal(newArmed)
        });
      }
    }
  }, [lastSignalData, onAlert, enabled]);

  const generateAlertMessage = (signal, fullData) => {
    if (!signal) return null;

    const signalType = signal.signal === 'BUY_CE' ? 'bullish' : 
                       signal.signal === 'BUY_PE' ? 'bearish' : 
                       signal.signal_type?.toLowerCase() || 
                       signal.signal?.toLowerCase() || 'trading';
    const strategy = signal.strategy_name || signal.reasoning?.strategy_name || 'trading strategy';
    
    let message = `Alert! I've detected a ${signalType} signal from the ${strategy}. `;
    
    // Handle reasoning - it might be an object or string
    if (signal.reasoning) {
      let reasoningText = '';
      if (typeof signal.reasoning === 'string') {
        reasoningText = signal.reasoning;
      } else if (typeof signal.reasoning === 'object') {
        // Extract text from reasoning object
        reasoningText = signal.reasoning.reasoning || 
                        signal.reasoning.description || 
                        signal.reasoning.summary || 
                        '';
      }
      if (reasoningText) {
        message += reasoningText + ' ';
      }
    }
    
    // Use stop_loss if available, otherwise try stop_loss_price
    const stopLoss = signal.stop_loss || signal.stop_loss_price;
    
    if (signal.entry_price) {
      message += `Entry: ₹${signal.entry_price.toLocaleString('en-IN', { maximumFractionDigits: 2 })}. `;
    }
    if (signal.target_price) {
      message += `Target: ₹${signal.target_price.toLocaleString('en-IN', { maximumFractionDigits: 2 })}. `;
    }
    if (stopLoss) {
      message += `Stop-loss: ₹${stopLoss.toLocaleString('en-IN', { maximumFractionDigits: 2 })}.`;
    }
    
    return message;
  };

  // Log connection status (for debugging)
  useEffect(() => {
    if (connectionError) {
      console.warn('AURA WebSocket error:', connectionError);
    }
  }, [connectionError]);

  return null; // This component doesn't render anything visible
}

